// src/components/TermsOfService.tsx
export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Terms of Service</h1>
          <p className="text-sm text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="prose max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">1. Acceptance of Terms</h2>
              <p className="mb-4 text-gray-700">
                By accessing and using AZTrainers ("Service", "Platform"), operated by Michael Mehta LLC, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Service, please do not use our service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">2. Description of Service</h2>
              <p className="mb-4 text-gray-700">
                AZTrainers, operated by Michael Mehta LLC, is a digital platform that connects fitness enthusiasts (customers) with certified personal trainers for in-person training sessions. Our service includes:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Trainer profiles and discovery</li>
                <li>Session booking and scheduling</li>
                <li>Payment processing</li>
                <li>Social features including posts, comments, and following</li>
                <li>Review and rating systems</li>
                <li>Communication tools between customers and trainers</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">3. User Accounts</h2>
              <p className="mb-4 text-gray-700">
                To access certain features of our service, you must create an account. You are responsible for:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>Providing accurate, current, and complete information</li>
                <li>Updating your information to keep it accurate and current</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">4. Trainer Services</h2>
              <h3 className="text-lg font-medium mb-3 text-gray-800">4.1 Trainer Qualifications</h3>
              <p className="mb-4 text-gray-700">
                Trainers on our platform are independently verified and must provide proof of certification and insurance. However, Michael Mehta LLC and AZTrainers does not employ trainers and is not responsible for their individual qualifications or conduct during sessions.
              </p>
              
              <h3 className="text-lg font-medium mb-3 text-gray-800">4.2 Training Sessions</h3>
              <p className="mb-4 text-gray-700">
                All training sessions are conducted between customers and independent trainer contractors. Michael Mehta LLC facilitates the connection and booking but is not a party to the training relationship.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">5. Booking and Payment</h2>
              <h3 className="text-lg font-medium mb-3 text-gray-800">5.1 Bookings</h3>
              <p className="mb-4 text-gray-700">
                When you book a session, you are entering into an agreement with the trainer. Payment is required at the time of booking. Bookings are subject to trainer approval.
              </p>
              
              <h3 className="text-lg font-medium mb-3 text-gray-800">5.2 Payment Processing</h3>
              <p className="mb-4 text-gray-700">
                Payments are processed through Stripe. We charge a platform fee for facilitating the connection and providing booking services. The majority of the payment goes directly to the trainer.
              </p>
              
              <h3 className="text-lg font-medium mb-3 text-gray-800">5.3 Cancellations and Refunds</h3>
              <p className="mb-4 text-gray-700">
                Cancellation policies vary by trainer but generally require 24 hours notice. If a trainer cancels or declines a booking, you will receive a full refund. Customer-initiated cancellations may be subject to trainer-specific policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">6. User Conduct</h2>
              <p className="mb-4 text-gray-700">You agree not to:</p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Use the service for any unlawful purpose or in violation of these terms</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Post inappropriate, offensive, or harmful content</li>
                <li>Impersonate others or provide false information</li>
                <li>Attempt to circumvent payment systems</li>
                <li>Share contact information to avoid platform fees</li>
                <li>Spam or send unsolicited communications</li>
                <li>Interfere with the service's functionality or security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">7. Content and Social Features</h2>
              <p className="mb-4 text-gray-700">
                Users may post content including photos, videos, and text.                 By posting content, you grant AZTrainers a non-exclusive license to use, display, and distribute your content on the platform. You retain ownership of your content and may delete it at any time.
              </p>
              <p className="mb-4 text-gray-700">
                We reserve the right to remove content that violates these terms or community standards.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">8. Health and Safety</h2>
              <p className="mb-4 text-gray-700">
                <strong>Important:</strong> Exercise involves inherent risks. Before beginning any fitness program:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Consult with your healthcare provider</li>
                <li>Disclose any medical conditions or limitations to your trainer</li>
                <li>Stop exercising if you experience pain or discomfort</li>
                <li>Follow your trainer's instructions and safety guidelines</li>
              </ul>
              <p className="mb-4 text-gray-700">
                You participate in training sessions at your own risk. Michael Mehta LLC and AZTrainers are not responsible for injuries that may occur during training sessions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">9. Liability and Disclaimers</h2>
              <p className="mb-4 text-gray-700">
                Michael Mehta LLC and AZTrainers serve as a platform connecting users and trainers. We do not provide fitness or health advice and are not responsible for:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>The quality, safety, or legality of training services</li>
                <li>Trainer conduct or qualifications beyond initial verification</li>
                <li>Injuries or damages occurring during training sessions</li>
                <li>Disputes between customers and trainers</li>
                <li>User-generated content accuracy or appropriateness</li>
              </ul>
              <p className="mb-4 text-gray-700">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, MICHAEL MEHTA LLC AND AZTRAINERS DISCLAIMS ALL WARRANTIES AND LIMIT LIABILITY FOR DAMAGES ARISING FROM USE OF THE SERVICE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">10. Privacy</h2>
              <p className="mb-4 text-gray-700">
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the service, to understand our practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">11. Termination</h2>
              <p className="mb-4 text-gray-700">
                Michael Mehta LLC may terminate or suspend your account and access to the service immediately, without prior notice, for conduct that we believe violates these Terms of Service or is harmful to other users, us, or third parties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">12. Changes to Terms</h2>
              <p className="mb-4 text-gray-700">
                We reserve the right to modify these terms at any time. We will provide notice of significant changes through the service or by email. Your continued use of the service after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">13. Contact Information</h2>
              <p className="mb-4 text-gray-700">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">Michael Mehta LLC</p>
                <p className="text-gray-700">Email: michaelmehtallc@gmail.com</p>
                <p className="text-gray-700">Address: 860 N McQueen rd, Chandler AZ, 85225</p>
                <p className="text-gray-700">Phone: 480-997-8691</p>
              </div>
            </section>
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">14. Governing Law</h2>
              <p className="mb-4 text-gray-700">
                These Terms shall be governed by and construed in accordance with the laws of the State of Arizona, without regard to its conflict of law provisions.
              </p>
              <p className="mb-4 text-gray-700">
                ---
              </p>
              <p className="mb-4 text-gray-700">
                © 2025 Michael Mehta LLC. All rights reserved.
              </p>
              
            </section>
          </div>
          
          
        </div>
      </div>
    </div>
  );
}